import { useEffect, useState } from 'react';

import { fetchPostIwocapayBuyerSpendingLimitRequest } from '@iwoca/lapi-client/edge';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';

import {
  useGetContext,
  useGetStateByStateKey,
} from '../../../../api/lending/lapiHooks';
import { iwocapayTelDisplay } from '../../../../constants.json';
import { useAccountNavigation } from '../../../../hooks/useAccountNavigation';
import { useQueryParam } from '../../../../hooks/useQueryParam';
import { createBuyerAccount } from '../../../../Pages/stateApi';
import { getPrimaryEmailAddressForCustomerState } from '../../../../Seller/AccessUsers/AccessUsers';
import logger from '../../../../utils/logger';
import {
  EmailFormInput,
  useEmailValidation,
} from '../../../components/EmailInput/EmailInput';
import { SIGNUP_BASE_PATH } from '../../../Signup/routes';
import { createState } from '../../../utils/customerState';
import { getOutstandingApplicationErrorMessage } from '../../../utils/outstandingApplicationError';
import { buildQueryString } from '../../../utils/queryParams';

export type FormValues = {
  emailAddress: string;
  marketingOptIn: boolean;
};

export const EmailDetails = () => {
  const { sellerHandle } = useParams();
  const site = useQueryParam('site');
  const validate = useEmailValidation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { state: customerState } = useGetStateByStateKey();
  const { context } = useGetContext();
  const { navigateToAccount } = useAccountNavigation();

  const isSignUpComplete = Boolean(context?.user_signup_finished);

  const initialFormValues: FormValues = {
    emailAddress: '',
    marketingOptIn: true,
  };

  const methods = useForm<FormValues>({
    defaultValues: initialFormValues,
    resolver: validate,
    mode: 'onChange',
  });

  const onSubmit = async ({
    values,
    stateKey,
  }: {
    values: FormValues;
    stateKey?: string;
  }) => {
    if (!sellerHandle) {
      throw Error('No seller handle found');
    }
    if (!stateKey) {
      await submitSpendingLimitNewCustomer(
        values.emailAddress,
        values.marketingOptIn,
        sellerHandle,
        site,
      );
    } else {
      await submitSpendingLimitReturningCustomer(
        stateKey,
        sellerHandle,
        isSignUpComplete,
        navigateToAccount,
        site,
      );
    }
  };

  const onHookFormSubmit = methods.handleSubmit(async (values: FormValues) => {
    await onSubmit({
      values,
      stateKey: customerState?.state_key,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).catch((error: any) => {
      logger.error(error.message, error);
      const outstandingApplicationMessage =
        getOutstandingApplicationErrorMessage(error);
      setSubmitError(
        outstandingApplicationMessage ??
          `Oops something went wrong. Please try again or give us a call on ${iwocapayTelDisplay}.`,
      );
    });
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onHookFormSubmit} noValidate>
        <EmailDetailsForm submitError={submitError} />
      </form>
    </FormProvider>
  );
};

const EmailDetailsForm = ({ submitError }: { submitError: string | null }) => {
  const { setValue: setFieldValue, trigger } = useFormContext<FormValues>();

  const { state: customerState } = useGetStateByStateKey();
  const loggedInCustomerEmailAddress =
    getPrimaryEmailAddressForCustomerState(customerState);

  useEffect(() => {
    setFieldValue('emailAddress', loggedInCustomerEmailAddress || '');
  }, [loggedInCustomerEmailAddress, setFieldValue, trigger]);

  return (
    <div>
      <EmailFormInput
        loggedIn={Boolean(loggedInCustomerEmailAddress)}
        isSpendingLimit
      />
      {submitError && <p className="m-0 font-bold text-error">{submitError}</p>}
    </div>
  );
};

const createSpendingLimitRequest = async ({
  stateKey,
  sellerHandle,
  site,
}: {
  stateKey: string;
  sellerHandle: string;
  site?: string | null;
}) => {
  if (!stateKey) {
    throw new Error('No stateKey was provided.');
  }
  return await fetchPostIwocapayBuyerSpendingLimitRequest({
    stateKey,
    body: {
      data: { seller_handle: sellerHandle, ...(site ? { site } : {}) },
    },
  });
};

async function submitSpendingLimitNewCustomer(
  emailAddress: string,
  marketingOptIn: boolean,
  sellerHandle: string,
  site?: string | null,
) {
  const customerState = await createBuyerAccount(
    createState(emailAddress, marketingOptIn),
  );
  const stateKey = customerState.data!.state_key;
  const spendingLimitResponse = await createSpendingLimitRequest({
    stateKey: stateKey,
    sellerHandle: sellerHandle,
    site: site,
  });
  const spending_limit_request_uuid = spendingLimitResponse.data!.uuid;
  window.location.href = `${SIGNUP_BASE_PATH}${buildQueryString({
    spending_limit_request_uuid: spending_limit_request_uuid,
  })}`;
}

async function submitSpendingLimitReturningCustomer(
  stateKey: string,
  sellerHandle: string,
  isSignUpComplete: boolean,
  navigateToAccount: (queryString?: string) => void,
  site?: string | null,
) {
  const spendingLimitResponse = await createSpendingLimitRequest({
    stateKey: stateKey,
    sellerHandle: sellerHandle,
    site: site,
  });
  const spending_limit_request_uuid = spendingLimitResponse.data!.uuid;

  if (isSignUpComplete) {
    navigateToAccount();
    return;
  }

  window.location.href = `${SIGNUP_BASE_PATH}${buildQueryString({
    spending_limit_request_uuid: spending_limit_request_uuid,
  })}`;
}
